import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Check, Pencil, X as XIcon, RefreshCw, Zap } from "lucide-react";
import { toast } from "sonner";
import { formatRequestAmount } from "./AdminTransactions";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";

const TYPE_TONES = {
  load: "bg-emerald-900/30 border-emerald-700/40 text-emerald-300",
  redeem: "bg-amber-900/30 border-amber-700/40 text-amber-300",
  admin_credit: "bg-emerald-900/30 border-emerald-700/40 text-emerald-300",
  admin_debit: "bg-amber-900/30 border-amber-700/40 text-amber-300",
  create_account: "bg-sky-900/30 border-sky-700/40 text-sky-300",
  reset_password: "bg-violet-900/30 border-violet-700/40 text-violet-300",
};
const STATUS_TONES = {
  pending: "bg-slate-800 border-white/10 text-slate-300",
  approved: "bg-emerald-900/30 border-emerald-700/40 text-emerald-300",
  rejected: "bg-red-900/30 border-red-700/40 text-red-300",
};

export default function AdminRequests() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState({});
  const [autoBusy, setAutoBusy] = useState(false);

  const load = useCallback(async () => {
    const q = filter === "all" ? "" : `?status_filter=${filter}`;
    const { data } = await api.get(`/admin/requests${q}`);
    setRequests(data);
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useAdminAutoRefresh(() => load());

  const act = async (req, status) => {
    if (busy[req.id]) return;
    let note = "";
    if (status === "approved" && req.type === "create_account") {
      note = promptAccountDetails(req.admin_note);
      if (!note) return;
    } else if (status === "approved" && req.type === "reset_password") {
      note = promptOptionalPasswordDetails(req.admin_note);
      if (note === null) return;
    }
    setBusy((b) => ({ ...b, [req.id]: true }));
    try {
      await api.put(`/admin/requests/${req.id}`, { status, admin_note: note });
      toast.success(`Request ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [req.id]: false }));
    }
  };

  const editDetails = async (req) => {
    if (busy[req.id]) return;
    const note = req.type === "create_account"
      ? promptAccountDetails(req.admin_note)
      : promptPasswordDetails(req.admin_note);
    if (!note) return;
    setBusy((b) => ({ ...b, [req.id]: true }));
    try {
      await api.put(`/admin/requests/${req.id}/details`, { admin_note: note });
      toast.success("Details updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [req.id]: false }));
    }
  };

  const runAutoProcess = async () => {
    if (autoBusy) return;
    setAutoBusy(true);
    try {
      const { data } = await api.post("/admin/requests/auto-process-player");
      const approved = Number(data?.approved || 0);
      const attempted = Number(data?.attempted || 0);
      toast.success(
        approved
          ? `Auto reset approved ${approved} request${approved === 1 ? "" : "s"}`
          : `Auto checked ${attempted} pending request${attempted === 1 ? "" : "s"}`
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAutoBusy(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 md:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Player requests</h1>
          <p className="text-sm text-slate-500 mt-1">{requests.length} {filter !== "all" && `(${filter})`}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runAutoProcess} disabled={autoBusy} data-testid="auto-process-player-requests"
            className="inline-flex items-center gap-2 rounded-md border border-emerald-500/30 px-3 py-2 text-xs font-bold text-emerald-200 hover:bg-emerald-500/10 disabled:opacity-50">
            <Zap className="w-3.5 h-3.5" />
            {autoBusy ? "Auto resetting..." : "Auto reset pending"}
          </button>
          <button onClick={load} data-testid="refresh-requests"
            className="p-2 rounded-md hover:bg-white/5 border border-white/10">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          {["pending", "approved", "rejected", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-3 py-1.5 text-xs rounded-md border capitalize ${
                filter === f ? "bg-white text-black border-white" : "border-white/10 text-slate-300 hover:bg-white/5"
              }`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-white/10 bg-[#121214]">
        <table className="min-w-[980px] w-full text-sm">
          <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Note</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} data-testid={`request-row-${r.id}`} className="border-t border-white/5">
                <td className="px-4 py-3">
                  <div className="font-medium">{r.username}</div>
                  <div className="text-xs text-slate-500">{r.email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs rounded-md border ${TYPE_TONES[r.type] || ""}`}>
                    {typeLabel(r.type)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{formatRequestAmount(r, "—")}</td>
                <td className="px-4 py-3 text-slate-400 text-xs max-w-xs truncate">{r.note || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs rounded-md border ${STATUS_TONES[r.status] || ""}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{new Date(r.created_at).toLocaleString("en-US")}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {r.status === "pending" ? (
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => act(r, "approved")}
                        disabled={busy[r.id]}
                        data-testid={`approve-${r.id}`}
                        className="p-1.5 rounded hover:bg-emerald-500/15 text-emerald-300 border border-emerald-700/30"
                        title="Approve"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => act(r, "rejected")}
                        disabled={busy[r.id]}
                        data-testid={`reject-${r.id}`}
                        className="p-1.5 rounded hover:bg-red-500/15 text-red-300 border border-red-700/30"
                        title="Reject"
                      >
                        <XIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-2">
                      <span className="block max-w-xs text-xs text-slate-500 italic whitespace-pre-wrap text-left">{r.admin_note || "—"}</span>
                      {["create_account", "reset_password"].includes(r.type) && (
                        <button
                          onClick={() => editDetails(r)}
                          disabled={busy[r.id]}
                          data-testid={`edit-details-${r.id}`}
                          className="p-1.5 rounded hover:bg-white/10 text-slate-300 border border-white/10 shrink-0"
                          title="Edit details"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {requests.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-500 py-10">No requests in this category.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function typeLabel(type) {
  return (type || "").replace(/_/g, " ");
}

function promptAccountDetails(existing = "") {
  const current = parseDetails(existing);
  const username = window.prompt("Game username to show customer:", current.username || "");
  if (username === null) return "";
  if (!username.trim()) {
    toast.error("Username is required");
    return "";
  }
  const password = window.prompt("Game password to show customer:", current.password || "");
  if (password === null) return "";
  if (!password.trim()) {
    toast.error("Password is required");
    return "";
  }
  const notes = window.prompt("Extra notes (optional):", current.notes || "");
  if (notes === null) return "";
  return [
    `Username: ${username.trim()}`,
    `Password: ${password.trim()}`,
    notes.trim() ? `Notes: ${notes.trim()}` : "",
  ].filter(Boolean).join("\n");
}

function promptPasswordDetails(existing = "") {
  const current = parseDetails(existing);
  const password = window.prompt("New password to show customer:", current.password || existing || "");
  if (password === null) return "";
  if (!password.trim()) {
    toast.error("New password is required");
    return "";
  }
  return `Password: ${password.trim()}`;
}

function promptOptionalPasswordDetails(existing = "") {
  const current = parseDetails(existing);
  const password = window.prompt("New password for customer. Leave blank to auto-reset if this provider supports it:", current.password || "");
  if (password === null) return null;
  return password.trim() ? `Password: ${password.trim()}` : "";
}

function parseDetails(text = "") {
  const out = {};
  text.split(/\r?\n/).forEach((line) => {
    const [rawKey, ...rest] = line.split(":");
    const key = rawKey.trim().toLowerCase();
    const value = rest.join(":").trim();
    if (!value) return;
    if (["username", "user", "login"].includes(key)) out.username = value;
    if (["password", "pass", "new password"].includes(key)) out.password = value;
    if (["notes", "note"].includes(key)) out.notes = value;
  });
  return out;
}
